import { lazy, Suspense, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { turningPointLabel, paradigmLabel } from '@/lib/narrative-labels';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useScenes, useAnalysisSummary, useNarrativeAnalysis,
  useBudgetEstimate, useBudgetLines, useBudgetParams, useEmotionalMap, useDiagnostics, useCharacters,
  useCharacterArcs, useConflicts, useThemes, useAggregatedCast,
  useStageStatuses, useStageSignals, useUpdateStageStatus, useProjectContent, useLocations,
} from '@/hooks/useScriptAnalysis';
import { dedupByCharacterName } from '@/lib/character-dedup';
import { fmtSemanas, weeksFromShootingDays } from '@/lib/fases-semanas';
import { fmtImporte } from '@/lib/formato-importes';
import { countCharacters, countCharactersWithDialogue, projectCharacters } from '@/lib/cast-counts';
import { intensityLevel } from '@/lib/narrative-levels';
import { IntensityLegend } from '@/components/narrativa/IntensityLegend';
import { computeDiagnosticScore, isSystemDiagnostic, type AreaScore } from '@/lib/diagnostic-score';
import {
  DASHBOARD_STAGES, deriveStageProgress, STAGE_PROGRESS_META,
  emptyStageSignals, type StageSignals,
} from '@/lib/stage-progress';

const ConstellationGraph = lazy(() => import('@/components/features/ConstellationGraph'));
import {
  Clapperboard, Users, MapPin, FileText, Wallet, Calendar,
  AlertTriangle, CheckCircle, ArrowRight, TrendingUp,
} from 'lucide-react';

function KPICard({ icon, value, label, sublabel, accent }: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  sublabel?: string;
  accent?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? 'border-pelip-primary/30 bg-pelip-primary/5' : 'border-pelip-border bg-pelip-bg-elev/50'}`}>
      <div className="flex items-center gap-3">
        <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${accent ? 'bg-pelip-primary/10 text-pelip-accent' : 'bg-pelip-bg-elev-2 text-pelip-text-muted'}`}>
          {icon}
        </div>
        <div>
          <p className={`font-display text-2xl font-light ${accent ? 'text-pelip-accent' : 'text-pelip-text'}`}>{value}</p>
          <p className="font-mono text-[10px] text-pelip-text-faint uppercase tracking-wider">{label}</p>
        </div>
      </div>
      {sublabel && <p className="text-[11px] text-pelip-text-muted mt-2 pl-12">{sublabel}</p>}
    </div>
  );
}

function EmotionalSparkline({ emotions, jobParam }: { emotions: any[]; jobParam: string }) {
  const maxIntensity = 10;
  return (
    <Link to={`/narrativa/emociones${jobParam}`} className="block">
      <Card className="border-pelip-border bg-pelip-bg-elev/50 hover:border-pelip-border-strong transition-colors cursor-pointer">
        <CardHeader className="pb-2">
          <CardTitle className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider flex items-center gap-2">
            <TrendingUp size={14} />
            Curva Emocional
            <ArrowRight size={12} className="ml-auto text-pelip-text-faint" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-[2px] h-16">
            {emotions.map((e: any) => (
              <div
                key={e.scene_number}
                className="flex-1 rounded-t transition-all hover:opacity-80"
                style={{
                  height: `${(e.intensity / maxIntensity) * 100}%`,
                  backgroundColor: intensityLevel(e.intensity).color,
                  minHeight: '2px',
                }}
                title={`Esc ${e.scene_number}: ${e.emotion} (${e.intensity}/10)`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-pelip-text-faint mt-1">
            <span>Esc 1</span>
            <span>Esc {emotions.length}</span>
          </div>
          <IntensityLegend className="mt-2" />
        </CardContent>
      </Card>
    </Link>
  );
}

const AREA_PATHS: Record<string, string> = {
  estructura: '/narrativa/estructura',
  personajes: '/narrativa/personajes',
  emociones: '/narrativa/emociones',
  ritmo: '/narrativa/ritmo',
  dialogos: '/narrativa/dialogos',
  temas: '/narrativa/temas',
};

// Muestra el diagnóstico narrativo REAL (motor computeDiagnosticScore, el mismo que
// DiagnosticoPage): nota global + por área. Nada calculado a ojo aquí.
function DiagnosticExpress({ score, areas, jobParam }: { score: number; areas: AreaScore[]; jobParam: string }) {
  const color = (v: number) => v >= 7 ? 'text-emerald-700 dark:text-emerald-400' : v >= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  const bar = (v: number) => v >= 7 ? 'bg-emerald-500' : v >= 5 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <Card className="border-pelip-border bg-pelip-bg-elev/50">
      <CardHeader className="pb-2">
        <CardTitle className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider">
          Diagnóstico Express
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <span className={`text-3xl font-bold font-mono ${color(score)}`}>
            {score.toFixed(1)}
          </span>
          <span className="text-pelip-text-faint text-sm">/10</span>
          <div className="flex-1 bg-pelip-bg-elev-2 rounded-full h-2 overflow-hidden">
            <div className={`h-full rounded-full ${bar(score)}`} style={{ width: `${score * 10}%` }} />
          </div>
        </div>
        {areas.map((a) => (
          <Link key={a.area} to={`${AREA_PATHS[a.area] || '/narrativa/diagnostico'}${jobParam}`} className={`flex items-center gap-2 group ${a.excluded ? 'opacity-45' : ''}`}>
            <span className="text-xs text-pelip-text-muted w-20">{a.label}</span>
            <div className="flex-1 bg-pelip-bg-elev-2 rounded-full h-1.5 overflow-hidden">
              <div className={`h-full rounded-full ${bar(a.score)}/70`} style={{ width: `${a.score * 10}%` }} />
            </div>
            <span className="text-xs font-mono text-pelip-text-faint w-7 text-right">{a.excluded ? '—' : a.score.toFixed(1)}</span>
            <ArrowRight size={10} className="text-slate-700 group-hover:text-pelip-text-muted transition-colors" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function ProductionProgress({ signals, jobParam }: { signals: StageSignals; jobParam: string }) {
  // Progreso REAL derivado de los datos (no porcentajes inventados): con datos
  // = 100%, en progreso = 50%, sin empezar = 0%.
  const pctOf = (key: string) => {
    const s = deriveStageProgress(key, signals);
    return s === 'has_data' ? 100 : s === 'in_progress' ? 50 : 0;
  };
  const items = [
    { label: 'Localizaciones', pct: pctOf('localizaciones'), path: '/produccion/localizaciones' },
    { label: 'Plan de rodaje', pct: pctOf('rodaje'), path: '/produccion/rodaje' },
    { label: 'Casting', pct: pctOf('casting'), path: '/produccion/casting' },
    { label: 'Diseño de producción', pct: pctOf('diseno_produccion'), path: '/produccion/desglose' },
    { label: 'Presupuesto', pct: pctOf('presupuesto'), path: '/presupuesto/icaa' },
  ];

  return (
    <Card className="border-pelip-border bg-pelip-bg-elev/50">
      <CardHeader className="pb-2">
        <CardTitle className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider">
          Progreso Producción
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item) => (
          <Link key={item.label} to={`${item.path}${jobParam}`} className="block group">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-pelip-text-muted group-hover:text-pelip-text-soft transition-colors">{item.label}</span>
              <span className="font-mono text-pelip-text-faint">{item.pct}%</span>
            </div>
            <div className="bg-pelip-bg-elev-2 rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-500/70 transition-all"
                style={{ width: `${item.pct}%` }}
              />
            </div>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function AlertsPanel({ scenes, diagnostics, jobParam }: { scenes: any[]; diagnostics: any[]; jobParam: string }) {
  const alerts: { icon: React.ReactNode; text: string; path: string; type: 'warn' | 'ok' }[] = [];

  // `incompleto` marca las escenas a las que el pipeline puso un desglose VACÍO para no
  // quedarse clavado cuando su texto no era legible. Tienen las mismas claves que un
  // desglose de verdad, así que sin mirar la marca contaban como desglosadas y el panel
  // no avisaba de nada: lo que falta en esas escenas falta también en el presupuesto.
  const unbroken = scenes.filter((s: any) =>
    !s.breakdown || Object.keys(s.breakdown).length === 0 || s.breakdown?.incompleto).length;
  if (unbroken > 0) {
    alerts.push({ icon: <AlertTriangle size={14} />, text: `${unbroken} escenas sin desglosar`, path: '/produccion/desglose', type: 'warn' });
  }

  // Problemas de alta severidad REALES del guion (excluye notas del pipeline/validador).
  const problems = diagnostics.filter((d: any) => d.type === 'problem' && d.severity === 'high' && !isSystemDiagnostic(d));
  if (problems.length > 0) {
    alerts.push({ icon: <AlertTriangle size={14} />, text: `${problems.length} problemas narrativos de alta severidad`, path: '/narrativa/diagnostico', type: 'warn' });
  }

  // (Se retira la alerta de "escenas en borrador": scenes.status es 'draft' por defecto
  // en TODAS las escenas — nunca cambia — así que disparaba siempre con el total. Falsa.)

  if (alerts.length === 0) {
    alerts.push({ icon: <CheckCircle size={14} />, text: 'Todo al día — análisis completo', path: '/', type: 'ok' });
  }

  return (
    <Card className="border-pelip-border bg-pelip-bg-elev/50">
      <CardHeader className="pb-2">
        <CardTitle className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider">
          Alertas / Próximos Pasos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {alerts.map((a, i) => (
          <Link
            key={i}
            to={`${a.path}${jobParam}`}
            className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${
              a.type === 'warn'
                ? 'text-amber-600 dark:text-amber-400 bg-amber-500/5 hover:bg-amber-500/10'
                : 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/5 hover:bg-emerald-500/10'
            }`}
          >
            {a.icon}
            <span className="flex-1">{a.text}</span>
            <ArrowRight size={12} className="text-pelip-text-faint" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}


const PROGRESS_CHIP: Record<string, string> = {
  faint: 'bg-pelip-bg-elev-2 text-pelip-text-faint border-pelip-border-strong',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  emerald: 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-500/30',
};

// Rejilla de etapas con estado AUTOMÁTICO (derivado de los datos) + check manual
// "revisado". Sin candados ni bloqueos: cualquier etapa es accesible siempre.
function StageGrid({
  signals, reviewedSet, jobParam, onToggleReview,
}: {
  signals: StageSignals;
  reviewedSet: Set<string>;
  jobParam: string;
  onToggleReview: (key: string, currentlyReviewed: boolean) => void;
}) {
  const withData = DASHBOARD_STAGES.filter(s => deriveStageProgress(s.key, signals) === 'has_data').length;

  return (
    <div className="rounded-xl border border-pelip-border bg-pelip-bg-elev/50 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider">Etapas del Proyecto</h2>
        <span className="text-xs text-pelip-text-faint">{withData} de {DASHBOARD_STAGES.length} con datos</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {DASHBOARD_STAGES.map(stage => {
          const progress = deriveStageProgress(stage.key, signals);
          const meta = STAGE_PROGRESS_META[progress];
          const reviewed = reviewedSet.has(stage.key);
          return (
            <div key={stage.key}
              className="relative rounded-lg border border-pelip-border bg-pelip-bg-elev-2/40 hover:border-blue-500/50 transition-colors">
              <Link to={`${stage.route}${jobParam}`} className="block p-3 pr-9">
                <div className="text-sm font-semibold text-pelip-text leading-tight">{stage.label}</div>
                <div className="text-[10px] text-pelip-text-faint mt-0.5">{stage.expert}</div>
                <div className="mt-2 flex items-center gap-1.5">
                  <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-medium ${PROGRESS_CHIP[meta.tone]}`}>
                    {meta.label}
                  </span>
                  {reviewed && (
                    <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400">Revisado</span>
                  )}
                </div>
              </Link>
              <button
                type="button"
                onClick={() => onToggleReview(stage.key, reviewed)}
                title={reviewed ? 'Quitar revisado' : 'Marcar como revisado'}
                className={`absolute top-2 right-2 p-1 rounded-md transition-colors ${
                  reviewed ? 'text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10' : 'text-pelip-text-faint hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2'
                }`}
              >
                <CheckCircle size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NextStepRecommendation({ signals, jobParam }: { signals: StageSignals; jobParam: string }) {
  // El siguiente paso = la primera etapa de la cadena que aún no tiene datos.
  const nextStage = DASHBOARD_STAGES.find(s => deriveStageProgress(s.key, signals) !== 'has_data');
  if (!nextStage) return null;

  return (
    <Link to={`${nextStage.route}${jobParam}`}
      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 transition-colors">
      <span className="text-blue-700 dark:text-blue-400 text-sm font-medium">Siguiente paso sugerido:</span>
      <span className="text-pelip-text font-semibold">{nextStage.label} →</span>
    </Link>
  );
}

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job');
  const jobParam = jobId ? `?job=${jobId}` : '';
  const queryClient = useQueryClient();

  // Cada vez que el Dashboard se monta o cambia el job, invalidar TODAS las
  // queries del proyecto para mostrar siempre el estado actual reflejando
  // cambios recientes en otras pestañas (presupuesto, rodaje, casting, etc.).
  useEffect(() => {
    if (!jobId) return;
    queryClient.invalidateQueries({ queryKey: ['scenes', jobId] });
    queryClient.invalidateQueries({ queryKey: ['characters', jobId] });
    queryClient.invalidateQueries({ queryKey: ['budget-estimate', jobId] });
    queryClient.invalidateQueries({ queryKey: ['budget-lines', jobId] });
    queryClient.invalidateQueries({ queryKey: ['budget-params', jobId] });
    queryClient.invalidateQueries({ queryKey: ['narrative-analysis', jobId] });
    queryClient.invalidateQueries({ queryKey: ['emotional-map', jobId] });
    queryClient.invalidateQueries({ queryKey: ['diagnostics', jobId] });
    queryClient.invalidateQueries({ queryKey: ['character-arcs', jobId] });
    queryClient.invalidateQueries({ queryKey: ['conflicts', jobId] });
    queryClient.invalidateQueries({ queryKey: ['themes', jobId] });
    queryClient.invalidateQueries({ queryKey: ['stage-statuses', jobId] });
    queryClient.invalidateQueries({ queryKey: ['stage-signals', jobId] });
    queryClient.invalidateQueries({ queryKey: ['project-content', jobId] });
    queryClient.invalidateQueries({ queryKey: ['analysis-summary', jobId] });
  }, [jobId, queryClient]);

  const summary = useAnalysisSummary(jobId);
  const { data: narrative } = useNarrativeAnalysis(jobId);
  const { data: budget } = useBudgetEstimate(jobId);
  const { data: budgetLines } = useBudgetLines(jobId);
  const { data: budgetParams } = useBudgetParams(jobId);
  const { data: emotions } = useEmotionalMap(jobId);
  const { data: diagnostics } = useDiagnostics(jobId);
  const { data: scenes } = useScenes(jobId);
  const { data: characters } = useCharacters(jobId);
  const { data: rawArcs } = useCharacterArcs(jobId);
  const arcs = dedupByCharacterName(rawArcs as any[], 'name');
  const { data: conflicts } = useConflicts(jobId);
  const { data: allThemes } = useThemes(jobId);
  const { data: stageStatuses } = useStageStatuses(jobId);
  const { data: stageSignals } = useStageSignals(jobId);
  const updateStage = useUpdateStageStatus();
  const { data: projectContent } = useProjectContent(jobId);
  const { data: locationsList } = useLocations(jobId);
  const { data: cast } = useAggregatedCast(jobId);

  if (!jobId) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <h2 className="text-2xl font-bold text-pelip-text mb-2">Ningún proyecto seleccionado</h2>
        <p className="text-pelip-text-muted mb-6">Selecciona un proyecto o sube un guión nuevo</p>
        <div className="flex gap-3">
          <Link
            to="/proyectos"
            className="px-6 py-3 bg-pelip-primary hover:bg-pelip-primary-hover text-white rounded-lg font-medium transition-colors"
          >
            Ver Proyectos
          </Link>
          <Link
            to="/subir"
            className="px-6 py-3 border border-pelip-border-strong hover:border-slate-600 text-pelip-text-soft rounded-lg font-medium transition-colors"
          >
            Subir Guión
          </Link>
        </div>
      </div>
    );
  }

  const logline = projectContent?.find((c: any) => c.content_type === 'logline')?.content;
  const synopsisShort = projectContent?.find((c: any) => c.content_type === 'synopsis_short')?.content;

  // Etapas marcadas manualmente como "revisado" (status='completed' en stage_status).
  const reviewedSet = new Set<string>(
    (stageStatuses || []).filter((s: any) => s.status === 'completed').map((s: any) => s.stage),
  );

  // Coste real = suma actual de budget_lines (refleja edits, seeds, ajustes).
  // No mostramos el production_cost auto-estimado del análisis porque suele venir alto;
  // se queda en '—' hasta que el usuario abra /presupuesto/icaa y se generen líneas.
  // La SS empresa SÍ suma al coste total, una sola vez, vía la línea agregada 10.02.01 del Cap 10.
  const linesTotal = (budgetLines || []).reduce((s: number, l: any) => s + Number(l.total || 0) + Number(l.dietas || 0), 0);
  const cost = linesTotal > 0
    ? `${fmtImporte(linesTotal)} €`
    : '—';
  // "Rodaje" = jornadas de rodaje. Misma cadena de prioridad que usa el estimador:
  // override manual → auto-sync del plan de rodaje → legacy budget_estimates.
  // (budget_estimates.shooting_weeks es una columna legacy que el flujo actual no puebla,
  //  por eso antes salía siempre en blanco aunque el plan tuviera jornadas.)
  const shootingDays =
    budgetParams?.shooting_days ||
    budgetParams?.shooting_days_computed ||
    budget?.shooting_days ||
    null;
  const weeks = shootingDays
    ? `${shootingDays} j`
    : (budget?.shooting_weeks ? `${budget.shooting_weeks} sem` : '—');
  const weeksSub = shootingDays ? `${fmtSemanas(weeksFromShootingDays(shootingDays))} semanas` : undefined;
  const intCount = scenes?.filter((s: any) => s.int_ext === 'INT').length ?? 0;
  const extCount = scenes?.filter((s: any) => s.int_ext === 'EXT').length ?? 0;
  // Fuente REAL de personajes: scene_cast agregado (la tabla `characters` está vacía →
  // antes mostraba el mismo nº como total y como "principales"). total = nº de
  // personajes; "con diálogo" = los que tienen líneas de diálogo reales.
  const characterTotals = projectCharacters(cast, scenes);
  const withDialogue = countCharactersWithDialogue(characterTotals);
  const personajesTotal = countCharacters(characterTotals) > 0
    ? countCharacters(characterTotals)
    : ((summary?.total_characters && Number(summary.total_characters) > 0) ? summary.total_characters : '—');

  // Diagnóstico narrativo REAL (mismo motor que DiagnosticoPage): base estructural +
  // ajuste por severidad, NO el ratio inventado fortalezas/total. Excluye notas del pipeline.
  const diagScore = computeDiagnosticScore({
    narrative, arcs: rawArcs, emotionalMap: emotions, themes: allThemes, conflicts,
    diagnostics, cast, sceneCount: scenes?.length ?? null,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-light text-pelip-text tracking-tight">Dashboard</h1>
          {narrative && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-blue-100 dark:bg-blue-600/20 text-blue-700 dark:text-blue-400 border-blue-500/30">{narrative.genre}</Badge>
              {narrative.subgenres?.map((s: string) => (
                <Badge key={s} variant="outline" className="border-pelip-border-strong text-pelip-text-muted text-xs">{s}</Badge>
              ))}
              <Badge variant="outline" className="border-pelip-border-strong text-pelip-text-muted text-xs">{paradigmLabel(narrative.paradigm)}</Badge>
            </div>
          )}
        </div>
        {narrative && (scenes?.length ?? 0) > 0 && (
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-xs px-3 py-1">
            Análisis Completo
          </Badge>
        )}
      </div>

      {/* Stage Grid — estado automático + check manual de revisado */}
      <StageGrid
        signals={stageSignals ?? emptyStageSignals()}
        reviewedSet={reviewedSet}
        jobParam={jobParam}
        onToggleReview={(key, currentlyReviewed) =>
          updateStage.mutate({ jobId, stage: key, status: currentlyReviewed ? 'available' : 'completed' })
        }
      />

      {/* Editorial Content */}
      {(logline || synopsisShort) && (
        <Card className="border-pelip-border bg-pelip-bg-elev/50">
          <CardContent className="p-5 space-y-3">
            {logline && (
              <div>
                <p className="font-mono text-[10px] text-pelip-text-faint uppercase tracking-wider mb-1">Logline</p>
                <p className="text-pelip-text-soft font-medium italic">"{logline}"</p>
              </div>
            )}
            {synopsisShort && (
              <div>
                <p className="font-mono text-[10px] text-pelip-text-faint uppercase tracking-wider mb-1">Sinopsis</p>
                <p className="text-sm text-pelip-text-muted leading-relaxed">{synopsisShort}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Next step recommendation */}
      <NextStepRecommendation signals={stageSignals ?? emptyStageSignals()} jobParam={jobParam} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard icon={<Clapperboard size={18} />} value={summary?.total_scenes ?? '—'} label="Escenas" sublabel={`${intCount} INT / ${extCount} EXT`} />
        <KPICard icon={<Users size={18} />} value={personajesTotal} label="Personajes" sublabel={`${withDialogue} con diálogo`} />
        <KPICard icon={<MapPin size={18} />} value={(locationsList && locationsList.length > 0) ? locationsList.length : (summary?.total_locations ?? '—')} label="Localizaciones" />
        <KPICard icon={<FileText size={18} />} value={summary?.total_pages ?? '—'} label="Páginas" />
        <KPICard icon={<Wallet size={18} />} value={cost} label="Presupuesto" accent />
        <KPICard icon={<Calendar size={18} />} value={weeks} label="Rodaje" sublabel={weeksSub} />
      </div>

      {/* Constellation Mini */}
      {arcs && arcs.length > 0 && allThemes && conflicts && (
        <Link to={`/narrativa/emociones${jobParam}`}>
          <Suspense fallback={<Skeleton className="h-[200px] w-full" />}>
            <ConstellationGraph
              characterArcs={arcs}
              themes={allThemes}
              conflicts={conflicts}
              compact
              height={200}
            />
          </Suspense>
        </Link>
      )}

      {/* Sparkline + Diagnostic + Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Emotional Sparkline — spans 1 col on large */}
        <div className="lg:col-span-1">
          {emotions && emotions.length > 0 ? (
            <EmotionalSparkline emotions={emotions} jobParam={jobParam} />
          ) : (
            <Card className="border-pelip-border bg-pelip-bg-elev/50 h-full flex items-center justify-center">
              <p className="text-pelip-text-faint text-sm py-8">Mapa emocional no disponible</p>
            </Card>
          )}
        </div>

        {/* Diagnostic Express */}
        <div className="lg:col-span-1">
          {diagnostics && diagnostics.length > 0 ? (
            <DiagnosticExpress score={diagScore.global} areas={diagScore.areas} jobParam={jobParam} />
          ) : (
            <Card className="border-pelip-border bg-pelip-bg-elev/50 h-full flex items-center justify-center">
              <p className="text-pelip-text-faint text-sm py-8">Diagnóstico no disponible</p>
            </Card>
          )}
        </div>

        {/* Production Progress */}
        <div className="lg:col-span-1">
          {scenes && characters ? (
            <ProductionProgress signals={stageSignals ?? emptyStageSignals()} jobParam={jobParam} />
          ) : (
            <Card className="border-pelip-border bg-pelip-bg-elev/50 h-full flex items-center justify-center">
              <p className="text-pelip-text-faint text-sm py-8">Progreso no disponible</p>
            </Card>
          )}
        </div>
      </div>

      {/* Alerts */}
      {scenes && diagnostics && (
        <AlertsPanel scenes={scenes} diagnostics={diagnostics} jobParam={jobParam} />
      )}

      {/* Narrative Tone */}
      {narrative && (
        <Card className="border-pelip-border bg-pelip-bg-elev/50">
          <CardHeader className="pb-2">
            <CardTitle className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider">Tono Narrativo</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-pelip-text-soft text-sm leading-relaxed">{narrative.tone}</p>
            {narrative.audience_estimate && (
              <p className="text-pelip-text-faint text-xs mt-3">{narrative.audience_estimate}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Turning Points */}
      {narrative?.turning_points?.length > 0 && (
        <Card className="border-pelip-border bg-pelip-bg-elev/50">
          <CardHeader className="pb-2">
            <CardTitle className="font-mono text-[10px] text-pelip-text-muted uppercase tracking-wider">Puntos de Giro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {narrative.turning_points.map((tp: any) => (
                <div key={tp.scene_number} className="flex items-start gap-3">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-600/20 text-blue-700 dark:text-blue-400 flex items-center justify-center text-xs font-bold font-mono">
                    {tp.scene_number}
                  </span>
                  <div>
                    <span className="text-xs text-pelip-text-faint uppercase">{turningPointLabel(tp.type)}</span>
                    <p className="text-sm text-pelip-text-soft">{tp.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
